export type ResourceTypeConfig = {
  label: string;
  sourceToggle: string;
  sourceCapacity?: string;
  unit?: 'Boards' | 'Sq Ft';
  // When set, the toggle can only be turned ON if this form field holds a
  // non-blank value (e.g. Bumper requires a Bumper Payment Type), and
  // `paymentTypeMessage` is shown otherwise.
  paymentTypeSource?: string;
  paymentTypeMessage?: string;
};

// Canonical description of a contractor's resource types. This is the single
// source of truth for the resource-type table UI AND the field lists derived
// below — add a type here and everything else follows.
export const RESOURCE_TYPE_CONFIG: ResourceTypeConfig[] = [
  {
    label: 'Hanger',
    sourceToggle: 'hangerAvailable',
    sourceCapacity: 'hangerCapacity',
    unit: 'Boards',
  },
  {
    label: 'Finisher',
    sourceToggle: 'finisherAvailable',
    sourceCapacity: 'finisherCapacity',
    unit: 'Boards',
  },
  {
    label: 'Sprayer',
    sourceToggle: 'sprayerAvailable',
    sourceCapacity: 'sprayerCapacity',
    unit: 'Boards',
  },
  {
    label: 'Trim',
    sourceToggle: 'trimAvailable',
    sourceCapacity: 'trimCapacity',
    unit: 'Sq Ft',
  },
  {
    label: 'Paint',
    sourceToggle: 'paintAvailable',
    sourceCapacity: 'paintCapacity',
    unit: 'Sq Ft',
  },
  {
    label: 'Bumper',
    sourceToggle: 'bumperAvailable',
    sourceCapacity: 'bumperCapacity',
    unit: 'Boards',
    paymentTypeSource: 'bumperPaymentType',
    paymentTypeMessage:
      'Bumper Payment Type must be filled before a Contractor can be assigned as a Bumper',
  },
  { label: 'Specialist', sourceToggle: 'specialistAvailable' },
];

// Returns the warning for the first guarded toggle that is ON while its
// required payment type is still blank/N/A, or undefined when all are valid.
export const getMissingPaymentTypeMessage = (
  values: Record<string, unknown>,
): string | undefined =>
  RESOURCE_TYPE_CONFIG.find(
    config =>
      config.paymentTypeSource &&
      values[config.sourceToggle] &&
      !values[config.paymentTypeSource],
  )?.paymentTypeMessage;

// Capacity columns only (Specialist has no capacity, so it drops out here).
export const CAPACITY_FIELDS: string[] = RESOURCE_TYPE_CONFIG.map(
  config => config.sourceCapacity,
).filter((field): field is string => Boolean(field));

// Every field a Regional Manager is allowed to write (all toggles + capacities).
// The edit form's `transform` uses this as the authoritative save whitelist.
export const RM_SAVE_FIELDS: string[] = RESOURCE_TYPE_CONFIG.flatMap(config =>
  [config.sourceToggle, config.sourceCapacity].filter(
    (field): field is string => Boolean(field),
  ),
);
